import React from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/**
 * Wartość 0–100 do progów badge (uwzględnia fullMark z API).
 * @param {{ score?: number | null; fullMark?: number }} axis
 */
function axisPercent(axis) {
  const sc = Number(axis.score);
  if (!Number.isFinite(sc)) return null;
  const full = Number(axis.fullMark);
  if (Number.isFinite(full) && full > 0) {
    return Math.min(100, Math.max(0, (sc / full) * 100));
  }
  return Math.min(100, Math.max(0, sc));
}

/**
 * @param {{
 *   show: boolean;
 *   onHide: () => void;
 *   axes?: Array<{
 *     axis?: string;
 *     subject?: string;
 *     score?: number | null;
 *     fullMark?: number;
 *     hasData?: boolean;
 *   }> | null;
 *   disclaimer?: string;
 * }} props
 */
export function HealthProfileBreakdownModal({ show, onHide, axes, disclaimer }) {
  const { t } = useTranslation();

  const list = Array.isArray(axes) ? axes : [];
  const hasRenderableRow = list.some((axis) => {
    if (!axis?.hasData) return false;
    return axisPercent(axis) != null;
  });

  const disclaimerTrimmed = typeof disclaimer === 'string' ? disclaimer.trim() : '';

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      scrollable
      contentClassName='health-profile-breakdown-modal-content border-0 shadow-lg rounded-3 overflow-hidden'
    >
      <Modal.Header closeButton className='health-profile-breakdown-modal__header border-0'>
        <Modal.Title as='h2' className='health-profile-breakdown-modal__title'>
          {t('dashboard.health-profile-modal-title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='health-profile-breakdown-modal__body'>
        {!hasRenderableRow ? (
          <p className='health-profile-radar-empty' role='status'>
            {t('dashboard.health-profile-empty')}
          </p>
        ) : (
          <>
            <details className='health-profile-modal-edu'>
              <summary className='health-profile-modal-edu__summary'>
                {t('dashboard.health-profile-modal-edu-summary')}
              </summary>
              <div className='health-profile-modal-edu__content'>
                <p className='health-profile-modal-edu__block-title'>
                  <strong>{t('dashboard.health-profile-modal-edu-h2')}</strong>
                </p>
                <p>{t('dashboard.health-profile-modal-edu-p1')}</p>
                <p>
                  <strong>100%</strong>
                  {' — '}
                  {t('dashboard.health-profile-modal-edu-optimal-desc')}
                </p>
                <p>
                  <strong>{t('dashboard.health-profile-modal-edu-low-label')}</strong>
                  {' — '}
                  {t('dashboard.health-profile-modal-edu-low-desc')}
                </p>
                <p className='health-profile-modal-edu__block-title health-profile-modal-edu__block-title--spaced'>
                  <strong>{t('dashboard.health-profile-modal-edu-why-q')}</strong>
                </p>
                <p>
                  {t('dashboard.health-profile-modal-edu-why-a1')}
                  <strong>{t('dashboard.health-profile-modal-edu-why-a-bold-mean')}</strong>
                  {t('dashboard.health-profile-modal-edu-why-a2')}
                  <strong>{t('dashboard.health-profile-modal-edu-why-a-principle')}</strong>
                  {t('dashboard.health-profile-modal-edu-why-a3')}
                </p>
                <p className='health-profile-modal-edu__block-title health-profile-modal-edu__block-title--spaced'>
                  <strong>{t('dashboard.health-profile-modal-edu-pillars-h')}</strong>
                </p>
                <ul className='health-profile-modal-edu__list'>
                  <li>{t('dashboard.health-profile-modal-edu-pillar-cardio')}</li>
                  <li>{t('dashboard.health-profile-modal-edu-pillar-meta')}</li>
                  <li>{t('dashboard.health-profile-modal-edu-pillar-organs')}</li>
                  <li>{t('dashboard.health-profile-modal-edu-pillar-mental')}</li>
                </ul>
              </div>
            </details>

            <div className='health-profile-radar-axes' role='list'>
              {list.map((axis, index) => {
                if (!axis?.hasData) return null;
                const pct = axisPercent(axis);
                if (pct == null) return null;

                let badgeMod = 'health-profile-radar-badge--poor';
                let statusKey = 'dashboard.health-profile-status-high-risk';

                if (pct >= 80) {
                  badgeMod = 'health-profile-radar-badge--good';
                  statusKey = 'dashboard.health-profile-status-good';
                } else if (pct >= 40) {
                  badgeMod = 'health-profile-radar-badge--moderate';
                  statusKey = 'dashboard.health-profile-status-moderate';
                }

                const rowKey = axis.axis ?? axis.subject ?? `axis-${index}`;

                return (
                  <div key={rowKey} className='health-profile-radar-axis-row' role='listitem'>
                    <span className='health-profile-radar-axis-label'>
                      {axis.subject ?? axis.axis ?? ''}
                    </span>
                    <div className='health-profile-radar-axis-meta'>
                      <span className='health-profile-radar-axis-pct'>{Math.round(pct)}%</span>
                      <span className={`health-profile-radar-badge ${badgeMod}`}>
                        {t(statusKey)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {disclaimerTrimmed ? (
          <p className='health-profile-radar-disclaimer' role='note'>
            {disclaimerTrimmed}
          </p>
        ) : null}
      </Modal.Body>
    </Modal>
  );
}
